import { useState, useCallback, useEffect } from "react";
import { DialogLibraryFlow } from "./DialogLibraryFlow";
import { useIsMobile } from "./useIsMobile";
import { Text } from "./tailwind/text";
import { XMarkIcon } from "@heroicons/react/16/solid";
import { api } from "../api";
import { Link } from "./tailwind/link";
import { GRADIENT_OPTIONS } from "../enums/gradient-backgrounds.enum";

export function BannerNewLibraryFlow({
  dataLibraryFlow = [],
  title = "New flows!",
  hasAutomations = false,
  user,
}) {
  const isMobile = useIsMobile();
  const [active, setActive] = useState(false);
  const [selectedFlow, setSelectedLibraryFlow] = useState(null);
  const toggleActive = useCallback(() => setActive((active) => !active), []);
  const [campaignName, setCampaignName] = useState("");
  const [isDismissed, setIsDismissed] = useState(false);

  const handleNameChange = (e) => {
    const value = e.target.value;
    setCampaignName(value);
  };

  const dismissDate = hasAutomations
    ? user?.dismissedLibraryFlowsAutomationsAt
    : user?.dismissedLibraryFlowsCampaignsAt;

  const visibleFlows = dataLibraryFlow.filter((flow) => {
    if (!dismissDate) return true;
    return new Date(flow.createdAt) > new Date(dismissDate);
  });

  if (
    isMobile ||
    !dataLibraryFlow ||
    dataLibraryFlow.length === 0 ||
    visibleFlows.length === 0 ||
    isDismissed
  ) {
    return null;
  }

  const handleFlowClick = (libraryFlow) => {
    setSelectedLibraryFlow(libraryFlow);
    setCampaignName(libraryFlow?.originalScenario?.name ?? "");
    toggleActive(true);
  };

  const handleDismissAll = async () => {
    // Fermeture immédiate côté front
    setIsDismissed(true);

    try {
      const now = new Date().toISOString();
      const updateField = hasAutomations
        ? "dismissedLibraryFlowsAutomationsAt"
        : "dismissedLibraryFlowsCampaignsAt";

      await api.user.update(user.id, {
        [updateField]: now,
      });
    } catch (error) {
      console.error("Error dismissing banner:", error);
      // Rollback en cas d'erreur
      setIsDismissed(false);
    }
  };

  return (
    <>
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 relative">
        <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between">
          <div className="flex flex-col xl:flex-col xl:gap-4 xl:w-64 xl:flex-shrink-0">
            <div className="flex flex-row xl:flex-col items-center xl:items-start mb-4 xl:mb-0">
              <Text className="text-lg font-semibold">{title}</Text>
              <Link
                href={`/${hasAutomations ? "automations" : "campaigns"}/new`}
                className="text-sm text-gray-500 hover:underline ml-4 xl:ml-0"
              >
                Browse library
              </Link>
            </div>
            <button
              onClick={handleDismissAll}
              className="text-gray-400 hover:text-gray-600 transition-colors absolute top-1 right-1"
              aria-label="Fermer le banner"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-4 gap-4 xl:flex-1 xl:mr-2">
            {visibleFlows.map((libraryFlow) => (
              <LibraryFlowCard
                key={libraryFlow.id}
                libraryFlow={libraryFlow}
                handleFlowClick={handleFlowClick}
              />
            ))}
          </div>
        </div>
      </div>

      {selectedFlow && (
        <DialogLibraryFlow
          active={active}
          toggleActive={toggleActive}
          selectedLibraryFlow={selectedFlow}
          campaignName={campaignName}
          handleNameChange={handleNameChange}
          hasAutomations={false}
        />
      )}
    </>
  );
}

function LibraryFlowCard({ libraryFlow, handleFlowClick }) {
  const randomIndex = Math.floor(Math.random() * GRADIENT_OPTIONS.length);
  const randomGradient = GRADIENT_OPTIONS[randomIndex];

  return (
    <div
      key={libraryFlow.id}
      className="rounded-customBorderRadius border shadow-shadowbuttonmin hover:shadow-shadowbuttonpink hover:cursor-pointer overflow-hidden w-full relative"
    >
      <div
        onClick={() => handleFlowClick(libraryFlow)}
        className="cursor-pointer"
      >
        <div className="w-full h-[190px] relative">
          <div className="w-full h-[190px] absolute inset-0">
            <div
              className="w-full h-full"
              style={{
                background: randomGradient,
              }}
            ></div>
          </div>
          <img
            src={libraryFlow.thumbnailUrl}
            alt={`Illustrative image of ${
              libraryFlow.isAutomation ? "the automation" : "the campaign"
            } ${libraryFlow.title}`}
            className="w-full h-[230px] object-cover object-center absolute top-[-40px] left-0 right-0"
          />
        </div>

        <div className="p-3 h-[4rem] absolute bottom-0 left-0 right-0">
          <Text className="text-base font-bold  leading-6">
            {libraryFlow.title}
          </Text>
        </div>
      </div>
    </div>
  );
}
